import React, { useCallback, useContext, useEffect, useRef, useState, startTransition, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useScreenSize } from 'src/customHooks/customHooks'

// Context
import { NotificationContext } from 'src/consumer/context/NotificationContextProvider'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { SetupContext } from '../../context/SetupContext'

// Components
import PrescriptionDataProvider from 'src/mentor/components/PrescriptionMakingPage/context/PrescriptionDataProvider'
import HeartbeatManager from 'src/mentor/layout/checkinHeartbeatManager'
import { LoadingSpinner } from '../Loaders'
import PrewallAndPaywall from '../PrewallAndPaywall/PrewallAndPaywall'
import { ClubbedAuthModal } from '../SignIn/ClubbedAuthComponents'
import { WrongSigninModal } from '../SignIn/WrongSigninComponents'
import JoinSessionScreen from './components/JoinSession/JoinSessionScreen'
import LiveSessionHeaderComponent from './components/LiveSessionHeaderComponent'
import LSRenderComponent from './components/LSRenderComponent/LSRenderComponent'
import SessionContainer from './components/SessionContainer/SessionContainer'
import InvalidParticipantErrorPage from './components/InvalidParticipantErrorPage'
import PractitionerAssessmentDrawer from 'src/mentor/components/PractitionerAssessment/PractitionerAssessmentDrawer'

// Hooks
import { useAgoraConnection } from './hooks/useAgoraConnection'
import { useDevicePermissions } from './hooks/useDevicePermissionsNew'
import { useNotesManagement } from './hooks/useNotesManagement'
import { useSessionRecording } from './hooks/useSessionRecording'
import { useSessionState } from './hooks/useSessionState'
import { useSessionStatusLogging } from './hooks/useSessionStatusLogging'

// Services
import { agoraService } from './services/agoraService'
import { sessionService } from './services/sessionService'

// Utils and Constants
import { selectPrescriptionViewSettingsData } from 'src/clinic/reducers/prescription/prescriptionReducer'
import {
  APP_CONSTANTS,
  BOOKING_SESSION_STATUS,
  PRESCRIPTION_BUILDER_VIEW_TYPES,
  allAvailableEventTypes,
  platformOptions,
} from 'src/consumer/constants'
import { SEOComponent } from 'src/consumer/helpers/utilFunctions'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { getPastPatientReports } from 'src/reducers/emr/services/pastPatientReports'
import { getTeacherProfileData } from 'src/reducers/teacherProfileData/services/teacherProfile'
import { selectTeacherProfileData } from 'src/reducers/teacherProfileData/teacherProfileDataReducer'
import { isTokenInvalidOrAbsent } from 'src/views/pages/login/ProtectedRoute'
import { 
  getActiveAndUpcomingEvents,
  AskBothCameraAndMicrophonePermissionsDialog,
  AskSinglePermissionDialog,
} from './LiveSessionPageComponents'

// Styles
import { LSPageParentContainer, LSVideoPartSingleText } from './LiveSessionPageStyles'

// Permissions dialogs - NEW SYSTEM
import { MediaPermissionsFlow } from 'src/components/MediaPermissions'
import { markBookedEventLog } from 'src/consumer/services/featureServices'
import { FLOW_TYPES, logSessionRoutingDiagnostics, resolveSessionRoutingContext } from 'src/live'
import ScreeningSectionsProvider from 'src/mentor/components/PrescriptionMakingPage/context/ScreeningSectionsProvider'
import { FLOW_STATUSES_FOR_LEAVING_SESSION } from 'src/consumer/components/LiveSessionPage/utils/sessionNavigationUtils'

// Content renderers are now handled by LSRenderComponent

// Re-export the checkAvailableDevices function for backward compatibility
export { checkAvailableDevices } from 'src/live/shell/devicePermissions/checkAvailableDevices'

export default function LiveSessionScreen({ withSlug, openedAsWebview }) {
  const token = localStorage.getItem('token')
  if (isTokenInvalidOrAbsent(token)) {
    return <PublicLiveSessionPage withSlug={withSlug} openedAsWebview={openedAsWebview} />
  } else {
    return <SecuredLiveSessionPage withSlug={withSlug} openedAsWebview={openedAsWebview} />
  }
}

function SecuredLiveSessionPage({ withSlug, openedAsWebview }) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { width: screenWidth } = useScreenSize()
  const location = useLocation()
  const { bookedEventId } = useParams()

  // Refs
  const therapyReportRef = useRef()
  const prescriptionRef = useRef()
  const screeningReportRef = useRef() // NEW: Ref for screening report
  const assessmentReportRef = useRef()

  // Context
  const { refreshNotifications, latestNotificationPayload } = useContext(NotificationContext)
  const { setupData } = useContext(SetupContext)
  const { profileData } = useContext(ProfileContext)

  // Redux selectors
  const teacherProfileStoreData = useSelector(selectTeacherProfileData)
  const prescriptionViewSettings = useSelector(selectPrescriptionViewSettingsData)


  // Custom hooks
  const sessionState = useSessionState()
  const agoraConnection = useAgoraConnection()
  const sessionRoutingContext = useMemo(() => {
    if (!sessionState.bookedEventFinalData) {
      return null
    }

    return resolveSessionRoutingContext({
      flowType: FLOW_TYPES.consumerLiveSession,
      sessionData: sessionState.bookedEventFinalData,
    })
  }, [sessionState.bookedEventFinalData])
  const legacyMeetingSrc =
    sessionRoutingContext?.legacyPlatformId || sessionState.bookedEventFinalData?.legacyPlatformId
  const bookedEventUiData = useMemo(() => {
    if (!sessionState.bookedEventFinalData || !legacyMeetingSrc) {
      return sessionState.bookedEventFinalData
    }

    return {
      ...sessionState.bookedEventFinalData,
      meetingSrc: legacyMeetingSrc,
    }
  }, [legacyMeetingSrc, sessionState.bookedEventFinalData])
  // Skip auto-check for offline sessions to prevent permission requests
  // CRITICAL: Only skip if we have data AND it's offline
  // If data is not loaded yet, also skip to prevent premature permission checks
  const isOfflineSession = legacyMeetingSrc === 'offline'
  const shouldSkipAutoCheck = isOfflineSession || !sessionState.bookedEventFinalData
  const devicePermissions = useDevicePermissions({ 
    skipAutoCheck: shouldSkipAutoCheck 
  })
  const sessionRecording = useSessionRecording()
  const notesManagement = useNotesManagement()

  // Session status logging with device states
  useSessionStatusLogging({
    sessionStarted: sessionState.sessionStarted,
    isCameraOpen: sessionState.isCameraOpen,
    isAudioOpen: sessionState.isAudioOpen,
    bookedEventId: sessionState.bookedEventFinalData?.id,
    userRole: sessionState.userRole,
  })

  // Derived state
  const isMountedOnAWebView = Boolean(
    sessionStorage.getItem('isThisWebviewSession') || openedAsWebview,
  )
  // Auto-join from mentor home (replicates old behavior)
  // When navigating from /mentor-home pages, auto-join non-video sessions
  const previousPage = location.state?.from
  const mentorProfileData = profileData?.teacher
  const teacherProfileData = teacherProfileStoreData?.data
  const prescriptionViewSettingsData = prescriptionViewSettings?.data
  const markCompleted = sessionState.bookedEventFinalData?.markedCompleted
  const eventType = sessionState.bookedEventFinalData?.eventType

  // Permission calculations
  const allowPrescribing =
    sessionState.userRole === 'mentor' &&
    sessionState.bookedEventFinalData?.patientId &&
    (getAllRolesInBoolean().isDoctor || eventType === allAvailableEventTypes.consultation) &&
    mentorProfileData?.allowedToPrescribe

  const allowTherapyReport =
    sessionState.userRole === 'mentor' &&
    sessionState.bookedEventFinalData?.patientId &&
    eventType === allAvailableEventTypes.therapySession

  const allowScreeningReport =
    sessionState.userRole === 'mentor' &&
    sessionState.bookedEventFinalData?.patientId &&
    eventType === allAvailableEventTypes.screening

  const allowAssessmentReport =
    sessionState.userRole === 'mentor' &&
    sessionState.bookedEventFinalData?.patientId &&
    eventType === allAvailableEventTypes.assessment

  const allowPractitionerAssessment =
    sessionState.userRole === 'mentor' && Boolean(sessionState.bookedEventFinalData?.patientId)

  const allowActivityPlanAssign =
    sessionState.userRole === 'mentor' && mentorProfileData?.services?.activityPlan

  const selectedPlatform = platformOptions?.find(
    (item) => item?.id === legacyMeetingSrc,
  )

  const sessionContainerRef = useRef()
  const [drawerOpenHandler, setDrawerOpenHandler] = useState(null)
  const [assessmentDrawerOpen, setAssessmentDrawerOpen] = useState(false)

  // Wrapper: when clicking Assessments tab open the drawer and set selection; otherwise normal
  const setSelectedMenuItemWithAssessment = useCallback(
    (itemId) => {
      if (itemId === 'assessments') {
        setAssessmentDrawerOpen(true)
        sessionState.setSelectedMenuItem('assessments')
      } else {
        sessionState.setSelectedMenuItem(itemId)
      }
    },
    [sessionState.setSelectedMenuItem],
  )

  // Callback to receive handleOpenDrawer from SessionContainer
  const handleOpenDrawerChange = useCallback((handler) => {
    setDrawerOpenHandler(() => handler)
  }, [])

  let username
  if (sessionState.userRole === 'panelist') {
    username = `${profileData?.name} (Panelist)` || 'User'
  } else {
    username = profileData?.name || 'User'
  }

  // Initialize page data
  const setupPage = useCallback(
    async ({ hideLoader = false, retryCount = 0, maxRetries = 2 } = {}) => {
      if (!hideLoader) {
        sessionState.setProfileDataLoading(true)
        sessionState.setBookedEventDataLoading(true)
      }

      try {
        const [eventResult, peopleInfoResponse] = await Promise.all([
          sessionService.getBookedEventDetails(bookedEventId, withSlug),
          sessionService.getPeopleInformation(bookedEventId),
        ])

        if (eventResult.success) {
          sessionState.setBookedEventFinalData(eventResult.bookedEvent)
          sessionState.setAuxiliaryData(eventResult.auxiliaryData)

          if (sessionState.clinicLocationId !== eventResult.bookedEvent?.clinicId) {
            sessionState.setClinicLocationId(eventResult.bookedEvent?.clinicId)
          }
          if (sessionState.clinicBrandId !== eventResult.bookedEvent?.clinicBrandId) {
            sessionState.setClinicBrandId(eventResult.bookedEvent?.clinicBrandId)
          }
          
          // Extract support QR code media ID from API response
          // Check both top-level (auxiliaryData) and bookedEvent for supportQrCodeMediaId
          const qrCodeMediaId = eventResult.auxiliaryData?.supportQrCodeMediaId || 
                                eventResult.bookedEvent?.supportQrCodeMediaId
          if (qrCodeMediaId) {
            sessionState.setSupportQrCodeMediaId(qrCodeMediaId)
          }

          // Process participant data
          const participantData = sessionService.processParticipantSummaries(
            eventResult.bookedEvent,
            peopleInfoResponse,
            profileData,
          )

          sessionState.setUserRole(participantData.userRole)
          agoraConnection.setPeerChatId(participantData.peerChatId)
          agoraConnection.setPeerChatName(participantData.peerChatName)
          agoraConnection.setPeerChatImage(participantData.peerChatImage)

          // Get mentor slots if mentor - DEFERRED (not needed until user views next events)
          if (participantData.userRole === 'mentor') {
            const loadMentorSlots = async () => {
              const mentorSlotsResult = await sessionService.getMentorBookedSlots(
                eventResult.bookedEvent,
              )
              if (mentorSlotsResult.success) {
                sessionState.setMentorEventsList(mentorSlotsResult.mentorSlots)
              }
            }
            
            // Defer this API call - not needed until user views next events
            if ('requestIdleCallback' in window) {
              requestIdleCallback(loadMentorSlots, { timeout: 3000 })
            } else {
              setTimeout(loadMentorSlots, 1000)
            }
          }

          // Initialize UID object
          agoraConnection.setUidObject(sessionService.generateUids())

          // Get teacher profile if needed - DEFERRED (not needed until user tries to prescribe)
          if (profileData?.authorities?.includes('ROLE_TEACHER')) {
            const loadTeacherProfile = async () => {
              await dispatch(getTeacherProfileData())
            }
            
            // Defer this API call - not needed until user tries to prescribe
            if ('requestIdleCallback' in window) {
              requestIdleCallback(loadTeacherProfile, { timeout: 2000 })
            } else {
              setTimeout(loadTeacherProfile, 500)
            }
          }

          // Prefetch Patient History tab data in background (lazy) once main report page is loaded
          const patientIdForHistory = eventResult.bookedEvent?.patientId
          const referenceBookedEventIdForHistory = eventResult.bookedEvent?.id
          if (patientIdForHistory && referenceBookedEventIdForHistory) {
            const prefetchHistory = () => {
              dispatch(getPastPatientReports({ patientId: patientIdForHistory, referenceBookedEventId: referenceBookedEventIdForHistory }))
            }
            if (typeof requestIdleCallback !== 'undefined') {
              requestIdleCallback(prefetchHistory, { timeout: 2500 })
            } else {
              setTimeout(prefetchHistory, 500)
            }
          }

          agoraConnection.setMyChatId(profileData?.id)
        } else {
          // Check if this is a network error that we should retry
          const isNetworkError = eventResult.errorData?.isNetworkError
          const shouldRetry = isNetworkError && retryCount < maxRetries
          
          if (shouldRetry) {
            const retryDelay = 1000 * (retryCount + 1) // Exponential backoff: 1s, 2s, 3s
            
            await new Promise(resolve => setTimeout(resolve, retryDelay))
            return setupPage({ hideLoader, retryCount: retryCount + 1, maxRetries })
          }
          
          // Check if this is an invalid participant error (code 6002)
          if (eventResult.errorData?.code === 6002 || 
              eventResult.errorData?.code === '6002' ||
              (eventResult.errorData?.message?.toLowerCase().includes('failed to fetch booked event') && 
               !eventResult.errorData?.isNetworkError)) {
            sessionState.setInvalidParticipantError({
              code: eventResult.errorData?.code || 6002,
              message: eventResult.errorData?.message || 'You are not a valid participant of this meeting',
              bookedEventId,
            })
            return // Exit early - don't show wrong account popup
          }
          
          if (eventResult.errorData?.clientUserLoginHint) {
            sessionState.setWrongAccountPopupData({
              clientUserLoginHint: eventResult.errorData.clientUserLoginHint,
              supportWhatsappNumber: eventResult.errorData.supportWhatsappNumber,
            })
          }
        }
      } catch (error) {
        // Error handled by error state
      } finally {
        sessionState.setProfileDataLoading(false)
        sessionState.setBookedEventDataLoading(false)
      }
    },
    [profileData, bookedEventId, withSlug, dispatch],
  )

  // const previousPage = location.state?.from

  const autoJoinFromMentorHome = useCallback(() => {
    if (!(sessionState.bookedEventDataLoading || sessionState.profileDataLoading)) {
      if (
        sessionState.userRole &&
        sessionState.bookedEventFinalData &&
        profileData &&
        !Boolean(sessionState.showRejoin) &&
        previousPage &&
        previousPage?.startsWith('/mentor-home')
      ) {
        // Auto-join triggered

        // Only auto-join for non-video calls (offline/phone consultations)
        // For video calls (aumhum), user must manually join
        if (selectedPlatform?.id === 'aumhum') {
          // Do nothing for video calls - require manual join
        } else {
          sessionState.setSessionStarted(true)
        }
      }
    }
  }, [
    sessionState.bookedEventDataLoading,
    sessionState.profileDataLoading,
    sessionState.userRole,
    sessionState.bookedEventFinalData,
    sessionState.showRejoin,
    sessionState.setSessionStarted,
    profileData,
    previousPage,
    selectedPlatform,
  ])

  useEffect(() => {
    autoJoinFromMentorHome()
  }, [sessionState.bookedEventDataLoading, sessionState.profileDataLoading, sessionState.userRole])

  // Menu items effect: show only tabs for the current event type (consultation / screening / therapy / assessment)
  useEffect(() => {
    const menu = []

    // Build menu in priority order (last items added appear first due to unshift)

    if (selectedPlatform?.id === 'aumhum') {
      menu.unshift({ label: 'Chat 💬', id: 'chat', secondary: true })
    }

    if (allowActivityPlanAssign) {
      menu.unshift({ label: 'Activity Plans', id: 'activity_plans', secondary: true })
    }

    // Consultation session only: Prescription, Rx, History, Vitals, Investigations
    if (eventType === allAvailableEventTypes.consultation && allowPrescribing) {
      // Print Rx button hidden - not working properly
      // menu.unshift({ label: 'Print Rx', id: 'print_rx', secondary: true })
      menu.unshift({ label: 'Rx', id: 'rx', secondary: true })
      if (sessionState?.auxiliaryData?.screeningReport) {
        menu.unshift({
          label: 'Screening Report',
          id: 'consultation_screening_report',
          secondary: true,
        })
      }
      menu.unshift({ label: 'History', id: 'patient_history', secondary: true })
      menu.unshift({ label: 'Vitals History', id: 'vitals', secondary: true })
      menu.unshift({ label: 'Investigations', id: 'investigations', secondary: true })
      menu.unshift({ label: 'Prescription', id: 'prescription' })
    }

    // Screening session only
    if (eventType === allAvailableEventTypes.screening && allowScreeningReport) {
      menu.unshift({ label: 'Report Preview/Print', id: 'report-preview', secondary: true })
      menu.unshift({ label: 'History', id: 'patient_history', secondary: true })
      menu.unshift({ label: 'Screening Report', id: 'screening_report' })
    }

    // Therapy session only
    if (eventType === allAvailableEventTypes.therapySession && allowTherapyReport) {
      menu.unshift({ label: 'Report Preview/Print', id: 'report-preview', secondary: true })
      menu.unshift({ label: 'History', id: 'patient_history', secondary: true })
      menu.unshift({ label: 'Therapy Report', id: 'therapy_report' })
    }

    // Assessment session only (no Prescription/Rx/Vitals/Investigations; no Assessments tab – tests are in launcher)
    if (eventType === allAvailableEventTypes.assessment && allowAssessmentReport) {
      menu.unshift({ label: 'Report Preview/Print', id: 'report-preview', secondary: true })
      menu.unshift({ label: 'History', id: 'patient_history', secondary: true })
      menu.unshift({ label: 'Assessment Report', id: 'assessment_report' })
    }

    // Assessments tab only for non-assessment sessions (consultation/therapy/screening); not on Assessment Report page
    if (eventType !== allAvailableEventTypes.assessment && allowPractitionerAssessment) {
      menu.push({ label: 'Assessments', id: 'assessments' })
    }

    // Add video call as first item if available (appears first in tabs)
    if (selectedPlatform?.id === 'aumhum') {
      menu.unshift({ label: 'Video Call', id: 'video_call' })
    }

    // Dedupe by id so we never have duplicate keys (e.g. patient_history added per report type)
    const seen = new Set()
    const dedupedMenu = menu.filter((item) => {
      if (seen.has(item.id)) return false
      seen.add(item.id)
      return true
    })

    sessionState.setMenuItemsList(dedupedMenu)

    // Set default selection: video call if available, otherwise first content item (not assessments)
    const hasVideoCall = selectedPlatform?.id === 'aumhum'
    const firstContentId = dedupedMenu?.find((m) => m.id !== 'video_call' && m.id !== 'assessments')?.id ?? dedupedMenu?.[0]?.id
    const defaultSelection = hasVideoCall ? 'video_call' : firstContentId
    sessionState.setSelectedMenuItem(defaultSelection)
  }, [
    sessionState.bookedEventFinalData,
    mentorProfileData,
    eventType,
    allowPrescribing,
    allowScreeningReport,
    allowTherapyReport,
    allowAssessmentReport,
    allowPractitionerAssessment,
    selectedPlatform,
    allowActivityPlanAssign,
  ])

  // Device permissions check
  // Use ref to track if we've already checked to prevent infinite loops
  const hasCheckedDevicesRef = useRef(false)
  const lastEventIdRef = useRef(null)
  const checkAvailableDevicesRef = useRef(devicePermissions.checkAvailableDevices)
  
  // Update ref when checkAvailableDevices changes
  useEffect(() => {
    checkAvailableDevicesRef.current = devicePermissions.checkAvailableDevices
  }, [devicePermissions.checkAvailableDevices])
  
  useEffect(() => {
    const eventId = sessionState.bookedEventFinalData?.eventId
    const meetingSrc = legacyMeetingSrc
    const platformId = selectedPlatform?.id
    
    // Reset check flag if event ID changes (new session)
    if (eventId !== lastEventIdRef.current) {
      hasCheckedDevicesRef.current = false
      lastEventIdRef.current = eventId
    }
    
    // Only check devices once per session for online meetings
    // Skip device permission checks for offline meetings to avoid browser prompts
    if (
      !hasCheckedDevicesRef.current &&
      sessionState.bookedEventFinalData &&
      platformId === 'aumhum' &&
      meetingSrc !== 'offline'
    ) {
      hasCheckedDevicesRef.current = true
      
      // Call checkAvailableDevices but don't await it to avoid blocking
      checkAvailableDevicesRef.current().catch((error) => {
        // Reset flag on error so we can retry
        hasCheckedDevicesRef.current = false
      })
    }
  }, [sessionState.bookedEventFinalData?.eventId, legacyMeetingSrc, selectedPlatform?.id])

  // Ensure body can scroll when viewing report (session not started)
  useEffect(() => {
    if (!sessionState.sessionStarted) {
      // Ensure body and html can scroll when viewing a report
      // Remove any inline styles that might prevent scrolling
      const originalBodyOverflow = document.body.style.overflow
      const originalBodyPosition = document.body.style.position
      const originalBodyHeight = document.body.style.height
      const originalHtmlOverflow = document.documentElement.style.overflow
      const originalHtmlHeight = document.documentElement.style.height
      
      // Explicitly enable scrolling
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.height = ''
      document.documentElement.style.overflow = ''
      document.documentElement.style.height = ''
      
      // Also ensure the scroll lock count is reset
      if (document.body.dataset.scrollLockCount) {
        delete document.body.dataset.scrollLockCount
      }
      
      return () => {
        // Restore original styles on cleanup
        document.body.style.overflow = originalBodyOverflow
        document.body.style.position = originalBodyPosition
        document.body.style.height = originalBodyHeight
        document.documentElement.style.overflow = originalHtmlOverflow
        document.documentElement.style.height = originalHtmlHeight
      }
    }
  }, [sessionState.sessionStarted])

  // Main setup effect
  useEffect(() => {
    if (profileData) {
      setupPage()
    }
  }, [profileData, setupPage])

  useEffect(() => {
    if (!sessionRoutingContext || !sessionState.bookedEventFinalData?.id) {
      return
    }

    logSessionRoutingDiagnostics({
      label: 'LiveSessionPage',
      flowType: FLOW_TYPES.consumerLiveSession,
      sessionData: sessionState.bookedEventFinalData,
      sessionRoutingDecision: sessionRoutingContext.sessionRoutingDecision,
      context: {
        bookedEventId: sessionState.bookedEventFinalData.id,
      },
    })
  }, [sessionRoutingContext, sessionState.bookedEventFinalData])

  // Auto-save notes effect
  useEffect(() => {
    const intervalId = setInterval(() => {
      notesManagement.handleSubmitNotes(sessionState.bookedEventFinalData, sessionState.userRole)
    }, 10000) // 10 seconds

    return () => {
      clearInterval(intervalId)
    }
  }, [notesManagement.handleSubmitNotes, sessionState.bookedEventFinalData, sessionState.userRole])

  // Recording status query effect
  useEffect(() => {
    const intervalId = setInterval(() => {
      sessionRecording.queryRecordingStatus(agoraConnection.uidObject)
    }, 60000) // 1 minute

    return () => {
      clearInterval(intervalId)
    }
  }, [sessionRecording.isSessionRecording])

  // Use refs to store device permissions to keep handleJoinRoom stable
  // Update refs directly during render - this is safe because refs don't trigger re-renders
  const devicePermissionsRef = useRef(devicePermissions)
  const sessionStateRef = useRef(sessionState)
  const selectedPlatformRef = useRef(selectedPlatform)
  const agoraConnectionRef = useRef(agoraConnection)
  
  // Update refs synchronously - refs don't cause re-renders, so this is safe
  devicePermissionsRef.current = devicePermissions
  sessionStateRef.current = sessionState
  selectedPlatformRef.current = selectedPlatform
  agoraConnectionRef.current = agoraConnection
  
  // Join room handler - now stable and won't be recreated
  const handleJoinRoom = useCallback(async () => {
    // Get latest values from refs
    let currentDevicePermissions = devicePermissionsRef.current
    let currentSessionState = sessionStateRef.current
    let currentSelectedPlatform = selectedPlatformRef.current
    let currentAgoraConnection = agoraConnectionRef.current
    
    // Defensive checks before joining
    if (!currentSessionState?.bookedEventFinalData) {
      // If data is still loading, wait a bit and retry
      if (currentSessionState?.bookedEventDataLoading || currentSessionState?.profileDataLoading) {
        // Wait for data to load (max 5 seconds)
        let retries = 0
        const maxRetries = 50 // 50 * 100ms = 5 seconds
        while (retries < maxRetries && !sessionStateRef.current?.bookedEventFinalData) {
          await new Promise(resolve => setTimeout(resolve, 100))
          retries++
          // Update refs to get latest state
          currentSessionState = sessionStateRef.current
        }
        // Get updated state after waiting
        currentSessionState = sessionStateRef.current
        currentSelectedPlatform = selectedPlatformRef.current
        currentDevicePermissions = devicePermissionsRef.current
        currentAgoraConnection = agoraConnectionRef.current
        
        if (!currentSessionState?.bookedEventFinalData) {
          return
        }
      } else {
        return
      }
    }
    
    if (!currentSelectedPlatform) {
      return
    }
    
    if (!currentSessionState?.userRole) {
      return
    }
    
    if (!currentAgoraConnection) {
      return
    }
    
    if (!currentAgoraConnection.handleJoinRoom) {
      return
    }
    
    try {
      const joinResult = await currentAgoraConnection.handleJoinRoom({
        bookedEventFinalData: currentSessionState.bookedEventFinalData,
        userRole: currentSessionState.userRole,
        username,
        userHasAllowedAudio: currentDevicePermissions?.userHasAllowedAudio ?? false,
        userHasAllowedVideo: currentDevicePermissions?.userHasAllowedVideo ?? false,
        selectedCamera: currentDevicePermissions?.selectedCamera,
        selectedMicrophone: currentDevicePermissions?.selectedMicrophone,
        setTokenLoading: currentSessionState.setTokenLoading,
        setSessionStarted: currentSessionState.setSessionStarted,
        setIsCameraOpen: currentSessionState.setIsCameraOpen,
        setIsAudioOpen: currentSessionState.setIsAudioOpen,
        selectedPlatform: currentSelectedPlatform,
      })
    } catch (error) {
      // Still set session started even if there's an error (for offline sessions)
      if (currentSelectedPlatform?.id !== 'aumhum') {
        currentSessionState.setSessionStarted(true)
      }
      currentSessionState.setTokenLoading(false)
      // Re-throw to allow caller to handle
      throw error
    }
  }, [username]) // Only depend on username which is stable

  const getHandleActionsCompletedMethodBasedOnSelectedTab = useCallback(() => {
    // Check the actually selected menu item, not just the primary one
    // This ensures we get the method from the component that's currently rendered
    const selectedItem = sessionState?.selectedMenuItem
    
    // Also check the primary menu item as fallback (for cases where selectedMenuItem might not be set)
    const currentPrimaryMenuItem =
      (sessionState.menuItemsList?.filter((item) => item?.id !== 'video_call') || [])?.[0]
    
    // Check selected item first, then fall back to primary menu item
    const itemToCheck = selectedItem || currentPrimaryMenuItem?.id
    
    if (itemToCheck === 'prescription') {
      if (prescriptionRef?.current?.savePrescriptionData) {
        return prescriptionRef.current.savePrescriptionData
      }
      return null
    }
    if (itemToCheck === 'therapy_report') {
      if (therapyReportRef?.current?.checkAndOpenModal) {
        return therapyReportRef.current.checkAndOpenModal
      }
      return null
    }
    if (itemToCheck === 'screening_report') {
      if (screeningReportRef?.current?.checkAndOpenModal) {
        return screeningReportRef.current.checkAndOpenModal
      }
      return null
    }
    if (itemToCheck === 'assessment_report') {
      if (assessmentReportRef?.current?.checkAndOpenModal) {
        return assessmentReportRef.current.checkAndOpenModal
      }
      return null
    }
    
    // Fallback: If menu-based check failed, try to find any available component
    // This handles edge cases where the menu state might not match the rendered component
    if (screeningReportRef?.current?.checkAndOpenModal) {
      return screeningReportRef.current.checkAndOpenModal
    }
    if (assessmentReportRef?.current?.checkAndOpenModal) {
      return assessmentReportRef.current.checkAndOpenModal
    }
    if (therapyReportRef?.current?.checkAndOpenModal) {
      return therapyReportRef.current.checkAndOpenModal
    }
    if (prescriptionRef?.current?.savePrescriptionData) {
      return prescriptionRef.current.savePrescriptionData
    }
    
    return null
  }, [sessionState?.selectedMenuItem, sessionState?.menuItemsList])

  // Leave room handler
  // Use a ref to store the drawer open function (setter pattern to avoid infinite loops)
  const openContentDrawerRef = useRef(null)
  const setSetOpenContentDrawerRef = useRef(sessionState.setSetOpenContentDrawer)
  
  // Update ref when setter changes (setState functions are stable, but update ref just in case)
  useEffect(() => {
    setSetOpenContentDrawerRef.current = sessionState.setSetOpenContentDrawer
  }, [sessionState.setSetOpenContentDrawer])
  
  // Expose the ref setter function - this is stable and won't change
  const setOpenContentDrawerRef = useCallback((setOpenFn) => {
    openContentDrawerRef.current = setOpenFn
    // Also update sessionState for backward compatibility
    // Use setTimeout to defer state update until after render completes
    // This prevents "Cannot update component during render" warnings
    if (setOpenFn && setSetOpenContentDrawerRef.current) {
      setTimeout(() => {
        startTransition(() => {
          setSetOpenContentDrawerRef.current(setOpenFn)
        })
      }, 0)
    }
  }, []) // Empty deps - use ref to access latest setter

  // Memoize camera/audio callbacks to prevent unnecessary re-renders
  // Also log device toggle events for monitoring
  const handleCameraClose = useCallback(() => {
    sessionState.setIsCameraOpen(false)
    if (sessionState.bookedEventFinalData?.id && sessionState.userRole) {
      markBookedEventLog(sessionState.bookedEventFinalData.id, 'CAMERA_OFF', sessionState.userRole, {
        camera: false,
        mic: sessionState.isAudioOpen,
      })
    }
  }, [sessionState.setIsCameraOpen, sessionState.bookedEventFinalData?.id, sessionState.userRole, sessionState.isAudioOpen])

  const handleCameraOpen = useCallback(() => {
    sessionState.setIsCameraOpen(true)
    if (sessionState.bookedEventFinalData?.id && sessionState.userRole) {
      markBookedEventLog(sessionState.bookedEventFinalData.id, 'CAMERA_ON', sessionState.userRole, {
        camera: true,
        mic: sessionState.isAudioOpen,
      })
    }
  }, [sessionState.setIsCameraOpen, sessionState.bookedEventFinalData?.id, sessionState.userRole, sessionState.isAudioOpen])

  const handleAudioClose = useCallback(() => {
    sessionState.setIsAudioOpen(false)
    if (sessionState.bookedEventFinalData?.id && sessionState.userRole) {
      markBookedEventLog(sessionState.bookedEventFinalData.id, 'MIC_OFF', sessionState.userRole, {
        camera: sessionState.isCameraOpen,
        mic: false,
      })
    }
  }, [sessionState.setIsAudioOpen, sessionState.bookedEventFinalData?.id, sessionState.userRole, sessionState.isCameraOpen])

  const handleAudioOpen = useCallback(() => {
    sessionState.setIsAudioOpen(true)
    if (sessionState.bookedEventFinalData?.id && sessionState.userRole) {
      markBookedEventLog(sessionState.bookedEventFinalData.id, 'MIC_ON', sessionState.userRole, {
        camera: sessionState.isCameraOpen,
        mic: true,
      })
    }
  }, [sessionState.setIsAudioOpen, sessionState.bookedEventFinalData?.id, sessionState.userRole, sessionState.isCameraOpen])

  // Cleanup function to stop all media tracks (both video and audio) from video call
  const cleanupMediaTracks = useCallback(() => {
    try {
      // Stop all tracks from any active media streams related to the video call
      // This includes tracks from getUserMedia, Agora local streams, and preview streams
      
      // 1. Clean up video elements in the video call container
      const videoCallContainer = document.querySelector('.agora-video-container, .LSPageImmediateVideoCallContainer, [data-video-container]')
      if (videoCallContainer) {
        const videoElements = videoCallContainer.querySelectorAll('video')
        videoElements.forEach((video) => {
          if (video.srcObject) {
            const stream = video.srcObject
            if (stream instanceof MediaStream) {
              stream.getTracks().forEach((track) => {
                if (track.readyState !== 'ended') {
                  track.stop()
                }
              })
            }
            video.srcObject = null
          }
        })
      }

      // 2. Clean up camera preview streams
      const cameraPreviewContainer = document.querySelector('[data-camera-preview]')
      if (cameraPreviewContainer) {
        const previewVideos = cameraPreviewContainer.querySelectorAll('video')
        previewVideos.forEach((video) => {
          if (video.srcObject) {
            const stream = video.srcObject
            if (stream instanceof MediaStream) {
              stream.getTracks().forEach((track) => {
                if (track.readyState !== 'ended') {
                  track.stop()
                }
              })
            }
            video.srcObject = null
          }
        })
      }

      // 3. Clean up audio elements that might have audio tracks
      const audioElements = document.querySelectorAll('audio')
      audioElements.forEach((audio) => {
        if (audio.srcObject) {
          const stream = audio.srcObject
          if (stream instanceof MediaStream) {
            stream.getTracks().forEach((track) => {
              if (track.readyState !== 'ended') {
                track.stop()
              }
            })
          }
          audio.srcObject = null
        }
      })

      // 4. Clean up any tracked streams in global scope (if used)
      if (window.localMediaStreams && Array.isArray(window.localMediaStreams)) {
        window.localMediaStreams.forEach((stream) => {
          if (stream instanceof MediaStream) {
            stream.getTracks().forEach((track) => {
              if (track.readyState !== 'ended') {
                track.stop()
              }
            })
          }
        })
        window.localMediaStreams = []
      }

      // 5. Force stop ALL active media tracks (both video and audio) that might be lingering
      // This is a comprehensive cleanup that finds all active tracks regardless of where they are
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        // Get all media devices to ensure we're aware of active tracks
        navigator.mediaDevices.enumerateDevices().then((devices) => {
          // This enumeration helps ensure we're aware of all devices
          // The actual track stopping happens above
        }).catch(() => {
          // Ignore errors during enumeration
        })
        
        // Additional cleanup: Stop any tracks that might be in getUserMedia streams
        // This catches tracks that aren't attached to any DOM element
        if (navigator.mediaDevices.getTracks) {
          // Some browsers support this directly
          try {
            navigator.mediaDevices.getTracks().forEach((track) => {
              if (track.readyState !== 'ended') {
                track.stop()
              }
            })
          } catch (e) {
            // Not all browsers support getTracks directly
          }
        }
      }

      // 6. Explicitly stop all audio tracks from any active streams
      // This is critical for ensuring microphone is released
      const allMediaElements = document.querySelectorAll('video, audio')
      allMediaElements.forEach((element) => {
        if (element.srcObject) {
          const stream = element.srcObject
          if (stream instanceof MediaStream) {
            stream.getAudioTracks().forEach((audioTrack) => {
              if (audioTrack.readyState !== 'ended') {
                audioTrack.stop()
              }
            })
            stream.getVideoTracks().forEach((videoTrack) => {
              if (videoTrack.readyState !== 'ended') {
                videoTrack.stop()
              }
            })
          }
        }
      })
    } catch (error) {
      console.error('Error cleaning up media tracks:', error)
    }
  }, [])

  // Handle ending only the video call (not the entire session)
  // This is called when user clicks the end call button in the video component
  const handleEndVideoCall = useCallback(() => {
    const roleId = agoraService.getRoleId(sessionState.userRole)
    agoraService.logSessionEvent(sessionState.bookedEventFinalData?.id, 'LEAVE_AGORA_CALL', roleId, {
      camera: sessionState.isCameraOpen,
      mic: sessionState.isAudioOpen,
    })

    // Cleanup media tracks before ending call
    cleanupMediaTracks()

    // Only end the video call, keep the session active
    sessionState.setJoinAgoraCall(false)
    sessionState.setShowRejoin(true)
    // Don't set sessionStarted to false - keep the session active
  }, [sessionState, cleanupMediaTracks])

  // Guard to prevent multiple simultaneous calls to markSessionAsCompleted
  const isMarkingCompletedRef = useRef(false)

  const handleLeaveRoom = useCallback(
    async (markAsCompleted = true) => {
      // Default to true - "Save & Leave Session" should always mark as completed
      let handleActionsAfterCompleted = getHandleActionsCompletedMethodBasedOnSelectedTab()
      
      // If handler is not available, wait a bit and try again (component might still be mounting)
      // Try multiple times with increasing delays to handle slow component mounting
      if (!handleActionsAfterCompleted) {
        for (let attempt = 0; attempt < 5; attempt++) {
          await new Promise(resolve => setTimeout(resolve, 100 * (attempt + 1)))
          handleActionsAfterCompleted = getHandleActionsCompletedMethodBasedOnSelectedTab()
          if (handleActionsAfterCompleted) {
            break
          }
        }
      }
      
      if (handleActionsAfterCompleted) {
        try {
          const flowStatus = await handleActionsAfterCompleted(markAsCompleted)
          if (flowStatus === FLOW_STATUSES_FOR_LEAVING_SESSION.stop) {
            return
          }
        } catch (error) {
          console.error('Error in handleActionsAfterCompleted:', error)
          // Continue with leaving the room even if save fails
          // User can still leave, but the report might not be saved
        }
      }

      const roleId = agoraService.getRoleId(sessionState.userRole)
      agoraService.logSessionEvent(sessionState.bookedEventFinalData?.id, 'LEAVE', roleId, {
        camera: sessionState.isCameraOpen,
        mic: sessionState.isAudioOpen,
      })

      // Cleanup media tracks before leaving room
      cleanupMediaTracks()

      sessionState.setJoinAgoraCall(false)
      sessionState.setSessionStarted(false)
      sessionState.setShowRejoin(true)

      if (markAsCompleted) {
        // Prevent multiple simultaneous calls
        if (isMarkingCompletedRef.current) {
          return
        }
        
        isMarkingCompletedRef.current = true
        try {
          const response = await sessionService.markSessionAsCompleted(sessionState.bookedEventFinalData)
          // Always refresh to show current state
          setupPage({ hideLoader: true })
        } catch (error) {
          // Network or other error - still refresh
          setupPage({ hideLoader: true })
        } finally {
          isMarkingCompletedRef.current = false
        }
      }
      
      // Navigate back to chamber room if we came from there
      if (location.state?.from === 'mentor-chamber-room' && location.state?.returnUrl) {
        // Set flag in sessionStorage for reconnection
        sessionStorage.setItem('returnFromLiveSession', 'true')
        // Navigate back to chamber room
        navigate(location.state.returnUrl)
      }
    },
    [sessionState, setupPage, getHandleActionsCompletedMethodBasedOnSelectedTab, location.state, navigate, cleanupMediaTracks],
  )

  useEffect(() => {
    if (sessionState.sessionStarted) {
      markBookedEventLog(sessionState.bookedEventFinalData.id, 'JOIN', sessionState.userRole, {
        camera: sessionState.isCameraOpen,
        mic: sessionState.isAudioOpen,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Note: isCameraOpen and isAudioOpen are intentionally not in deps
    // We only want to log JOIN once when session starts, capturing the initial device states
  }, [sessionState.sessionStarted, sessionState.bookedEventFinalData, sessionState.userRole])

  // Cleanup media tracks when leaving the call or component unmounts
  useEffect(() => {
    // Cleanup when joinAgoraCall becomes false
    if (!sessionState.joinAgoraCall) {
      // Small delay to ensure Agora cleanup completes first
      const cleanupTimer = setTimeout(() => {
        cleanupMediaTracks()
      }, 100)
      
      return () => {
        clearTimeout(cleanupTimer)
      }
    }
  }, [sessionState.joinAgoraCall, cleanupMediaTracks])

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      cleanupMediaTracks()
    }
  }, [cleanupMediaTracks])

  // Content renderer - now using LSRenderComponent for cleaner separation of concerns
  const renderWhatINeed = useCallback(
    (selectedMenuItem, onClose) => (
      <LSRenderComponent
        menuItemsList={sessionState.menuItemsList}
        selectedMenuItem={selectedMenuItem}
        sessionData={{
          bookedEventFinalData: sessionState.bookedEventFinalData,
          auxiliaryData: sessionState.auxiliaryData,
          moveToPrintPrescriptionScreen: sessionState.moveToPrintPrescriptionScreen,
          setMoveToPrintPrescriptionScreen: sessionState.setMoveToPrintPrescriptionScreen,
          newWantedMenuItemId: sessionState.newWantedMenuItemId,
          setNewWantedMenuItemId: sessionState.setNewWantedMenuItemId,
          setSelectedMenuItem: sessionState.setSelectedMenuItem,
          drawerOpenHandler, // Pass drawerOpenHandler to LSRenderComponent
        }}
        mentorData={mentorProfileData}
        teacherData={teacherProfileData}
        prescriptionState={{
          prescriptionContentValue: sessionState.prescriptionContentValue,
          setPrescriptionContentValue: sessionState.setPrescriptionContentValue,
        }}
        onClose={onClose}
        callbacks={{
          handleLeaveRoom,
          setupPage,
        }}
        ref={{ prescriptionRef, therapyReportRef, screeningReportRef, assessmentReportRef }}
      />
    ),
    [
      sessionState.bookedEventFinalData,
      sessionState.auxiliaryData,
      sessionState.moveToPrintPrescriptionScreen,
      sessionState.setMoveToPrintPrescriptionScreen,
      sessionState.newWantedMenuItemId,
      sessionState.setNewWantedMenuItemId,
      sessionState.setSelectedMenuItem,
      sessionState.prescriptionContentValue,
      sessionState.setPrescriptionContentValue,
      mentorProfileData,
      teacherProfileData,
      handleLeaveRoom,
      setupPage,
      drawerOpenHandler, // Include drawerOpenHandler in dependencies
    ],
  )

  // Get next events for mentor
  let nextEvents
  if (sessionState.mentorEventsList) {
    nextEvents = getActiveAndUpcomingEvents(
      sessionState.mentorEventsList,
      5,
      sessionState.bookedEventFinalData,
    )
  }

  // Invalid participant error state - show error page
  if (sessionState.invalidParticipantError) {
    return (
      <InvalidParticipantErrorPage 
        bookedEventId={bookedEventId}
      />
    )
  }

  // Chat props for video call
  const chatProps = {
    profileData,
    peerChatId: agoraConnection.peerChatId,
    peerChatName: agoraConnection.peerChatName,
    peerChatImage: agoraConnection.peerChatImage,
  }

  return (
    <LSPageParentContainer sessionStarted={sessionState.sessionStarted}>
      <WrongSigninModal
        open={
          sessionState.wrongAccountPopupData &&
          Object.keys(sessionState.wrongAccountPopupData).length > 0
        }
        onClose={() => {
          // Cannot close this dialog
        }}
        noCloseButton={true}
        rightAccountData={sessionState.wrongAccountPopupData}
        openPageLink={window.location.href}
      />
      <ScreeningSectionsProvider>
        <PrescriptionDataProvider>
          {(sessionState.bookedEventDataLoading || sessionState.profileDataLoading) && (
            <LoadingSpinner bgColor="#F5F5F599" dark />
          )}
          {!(sessionState.bookedEventDataLoading || sessionState.profileDataLoading) && (<>
          {/* Header */}
          {sessionState.sessionStarted ? (
            <LiveSessionHeaderComponent
              bookedEventFinalData={bookedEventUiData}
              mentorProfileData={mentorProfileData}
              teacherProfileData={teacherProfileData}
              userRole={sessionState.userRole}
              isSessionRecording={sessionRecording.isSessionRecording}
              isRecordingProcessWaiting={sessionRecording.isRecordingProcessWaiting}
              handleRecordingButtonClick={() =>
                sessionRecording.handleRecordingButtonClick(
                  sessionState.bookedEventFinalData,
                  agoraConnection.uidObject,
                  agoraConnection.setUidObject,
                )
              }
              handleSeeAllSessions={() => {
                if (sessionState.userRole === 'mentor') {
                  if (sessionState.clinicLocationId) {
                    navigate(
                      `/mentor-home/my-clinic-associations/${sessionState.clinicBrandId}/${sessionState.clinicLocationId}`,
                    )
                  } else {
                    navigate('/mentor-home/my-calendar')
                  }
                } else {
                  navigate('/my-live-sessions')
                }
              }}
              handleLeaveRoom={handleLeaveRoom}
              changeLayoutFunction={agoraConnection.changeLayoutFunction}
              isLayoutPinned={sessionState.isLayoutPinned}
              anchorElForHeaderMenu={sessionState.anchorElForHeaderMenu}
              setAnchorElForHeaderMenu={sessionState.setAnchorElForHeaderMenu}
              auxiliaryData={sessionState.auxiliaryData}
              selectedPlatform={selectedPlatform}
              setDrawerForMoreOptionsOpenMobile={sessionState.setDrawerForMoreOptionsOpenMobile}
              setTimeElapsedInSeconds={sessionState.setTimeElapsedInSeconds}
              timeElapsedInSeconds={sessionState.timeElapsedInSeconds}
              sessionStarted={sessionState.sessionStarted}
              therapyReportRef={therapyReportRef}
              hideEndSessionButton={
                prescriptionViewSettingsData?.prescriptionBuilderViewType ===
                PRESCRIPTION_BUILDER_VIEW_TYPES.tabletView
              }
              isAlreadyMarkedAsCompleted={
                sessionState.bookedEventFinalData?.sessionStatus ===
                BOOKING_SESSION_STATUS.MARKED_COMPLETED
              }
              onOpenContentDrawer={() => {
                if (openContentDrawerRef.current) {
                  openContentDrawerRef.current()
                }
              }}
              // Tabs props for integrated header
              menuItemsList={sessionState.menuItemsList}
              selectedMenuItem={sessionState.selectedMenuItem}
              setSelectedMenuItem={setSelectedMenuItemWithAssessment}
              actionButtons={(() => {
                // Calculate action buttons similar to SessionContainer
                const currentItem = sessionState.menuItemsList?.find((item) => item.id === sessionState.selectedMenuItem)
                const isPrimarySection = currentItem && !currentItem.secondary
                if (!isPrimarySection) return { showSaveButtons: false }
                if (sessionState.selectedMenuItem === 'therapy_report' && therapyReportRef?.current) {
                  return {
                    showSaveButtons: true,
                    save: () => therapyReportRef.current?.saveTherapyReport?.(),
                    // Print/Save & Print buttons removed from editing page
                    isLoading: therapyReportRef.current?.prescriptionSaveLoading || false,
                  }
                }
                if (sessionState.selectedMenuItem === 'screening_report' && screeningReportRef?.current) {
                  return {
                    showSaveButtons: true,
                    save: () => screeningReportRef.current?.saveScreeningReport?.(),
                    saveAndPrint: () => screeningReportRef.current?.saveAndPrint?.(),
                    isLoading: screeningReportRef.current?.prescriptionSaveLoading || false,
                  }
                }
                if (sessionState.selectedMenuItem === 'assessment_report') {
                  return { showSaveButtons: false }
                }
                return { showSaveButtons: false }
              })()}
              therapyReportStatus={therapyReportRef?.current?.therapyReportStatus || null}
              onSettingsClick={
                sessionState.selectedMenuItem === 'therapy_report' ||
                sessionState.selectedMenuItem === 'screening_report' ||
                sessionState.selectedMenuItem === 'assessment_report'
                  ? () => {
                      if (sessionState.selectedMenuItem === 'therapy_report' && therapyReportRef?.current?.openSettings) {
                        therapyReportRef.current.openSettings()
                      } else if (sessionState.selectedMenuItem === 'screening_report' && screeningReportRef?.current?.openSettings) {
                        screeningReportRef.current.openSettings()
                      } else if (sessionState.selectedMenuItem === 'assessment_report' && assessmentReportRef?.current?.openSettings) {
                        assessmentReportRef.current.openSettings()
                      }
                    }
                  : undefined
              }
              onOpenDrawer={drawerOpenHandler}
            />
          ) : (
            <JoinSessionScreen
              isMountedOnAWebView={isMountedOnAWebView}
              navigate={navigate}
              bookedEventFinalData={bookedEventUiData}
              userRole={sessionState.userRole}
              auxiliaryData={sessionState.auxiliaryData}
              showRejoin={sessionState.showRejoin}
              timeElapsedInSeconds={sessionState.timeElapsedInSeconds}
              availableCameras={devicePermissions.availableCameras}
              availableMicrophones={devicePermissions.availableMicrophones}
              cameraError={devicePermissions.cameraError}
              microphoneError={devicePermissions.microphoneError}
              selectedCamera={devicePermissions.selectedCamera}
              selectedMicrophone={devicePermissions.selectedMicrophone}
              setGiveCameraPermissionDialogOpen={
                devicePermissions.setGiveCameraPermissionDialogOpen
              }
              setGiveMicrophonePermissionDialogOpen={
                devicePermissions.setGiveMicrophonePermissionDialogOpen
              }
              setSelectedCamera={devicePermissions.setSelectedCamera}
              setSelectedMicrophone={devicePermissions.setSelectedMicrophone}
              setUserHasAllowedAudio={devicePermissions.setUserHasAllowedAudio}
              setUserHasAllowedVideo={devicePermissions.setUserHasAllowedVideo}
              tokenLoading={sessionState.tokenLoading}
              userHasAllowedAudio={devicePermissions.userHasAllowedAudio}
              userHasAllowedVideo={devicePermissions.userHasAllowedVideo}
              selectedPlatform={selectedPlatform}
              handleJoinRoom={handleJoinRoom}
              handleSeeAllSessions={() => {
                if (sessionState.userRole === 'mentor') {
                  if (sessionState.clinicLocationId) {
                    navigate(
                      `/mentor-home/my-clinic-associations/${sessionState.clinicBrandId}/${sessionState.clinicLocationId}`,
                    )
                  } else {
                    navigate('/mentor-home/my-calendar')
                  }
                } else {
                  navigate('/my-live-sessions')
                }
              }}
              getProcessAndSetEventData={setupPage}
              nextEvents={nextEvents}
              markCompleted={markCompleted}
            />
          )}

          {/* Main Session Container */}
          <SessionContainer
            ref={sessionContainerRef}
            onOpenDrawerChange={handleOpenDrawerChange}
            sessionStarted={sessionState.sessionStarted}
            menuItemsList={sessionState.menuItemsList}
            bookedEventInfo={sessionState.bookedEventFinalData}
            selectedMenuItem={sessionState.selectedMenuItem}
            auxiliaryData={sessionState.auxiliaryData}
            setSelectedMenuItem={setSelectedMenuItemWithAssessment}
            renderWhatINeed={renderWhatINeed}
            screenWidth={screenWidth}
            eventType={eventType}
            markedCompleted={markCompleted}
            userRole={sessionState.userRole}
            joinAgoraCall={sessionState.joinAgoraCall}
            therapyReportRef={therapyReportRef}
            prescriptionRef={prescriptionRef}
            screeningReportRef={screeningReportRef}
            assessmentReportRef={assessmentReportRef}
            agoraRtcProps={agoraConnection.agoraRtcProps}
            agoraRtmProps={agoraConnection.agoraRtmProps}
            onCameraClose={handleCameraClose}
            onCameraOpen={handleCameraOpen}
            onAudioClose={handleAudioClose}
            onAudioOpen={handleAudioOpen}
            handleLeaveRoom={handleLeaveRoom}
            handleEndVideoCall={handleEndVideoCall}
            showRejoin={sessionState.showRejoin}
            // Device permissions for camera preview
            selectedCamera={devicePermissions.selectedCamera}
            selectedMicrophone={devicePermissions.selectedMicrophone}
            userHasAllowedAudio={devicePermissions.userHasAllowedAudio}
            userHasAllowedVideo={devicePermissions.userHasAllowedVideo}
            microphoneError={devicePermissions.microphoneError}
            cameraError={devicePermissions.cameraError}
            setUserHasAllowedAudio={devicePermissions.setUserHasAllowedAudio}
            setUserHasAllowedVideo={devicePermissions.setUserHasAllowedVideo}
            setGiveCameraPermissionDialogOpen={devicePermissions.setGiveCameraPermissionDialogOpen}
            setGiveMicrophonePermissionDialogOpen={devicePermissions.setGiveMicrophonePermissionDialogOpen}
            meetingSrc={legacyMeetingSrc}
            // Available devices for settings dropdown
            availableCameras={devicePermissions.availableCameras}
            availableMicrophones={devicePermissions.availableMicrophones}
            setSelectedCamera={devicePermissions.setSelectedCamera}
            setSelectedMicrophone={devicePermissions.setSelectedMicrophone}
            drawerForMoreOptionsOpenMobile={sessionState.drawerForMoreOptionsOpenMobile}
            setDrawerForMoreOptionsOpenMobile={sessionState.setDrawerForMoreOptionsOpenMobile}
            chatProps={chatProps}
            joinAgoraCallFunction={() =>
              agoraConnection.joinAgoraCallFunction(
                sessionState.bookedEventFinalData,
                sessionState.userRole,
                sessionState.setJoinAgoraCall,
                sessionState.isCameraOpen,
                sessionState.isAudioOpen,
              )
            }
            timeElapsedInSeconds={sessionState.timeElapsedInSeconds}
            setTimeElapsedInSeconds={sessionState.setTimeElapsedInSeconds}
            leaveRoom={handleLeaveRoom}
            hidePreviewButton={sessionState.hidePreviewButton}
            moveToPrintPrescriptionScreen={sessionState.moveToPrintPrescriptionScreen}
            setMoveToPrintPrescriptionScreen={sessionState.setMoveToPrintPrescriptionScreen}
            changeLayoutFunction={agoraConnection.changeLayoutFunction}
            onContentDrawerOpenChange={setOpenContentDrawerRef}
            // Support QR code from API
            supportQrCodeMediaId={sessionState.supportQrCodeMediaId}
          />

          {/* Practitioner assessment drawer - /l/* full-page (e.g. offline) sessions */}
          {sessionState.sessionStarted && sessionState.bookedEventFinalData && (
            <PractitionerAssessmentDrawer
              open={assessmentDrawerOpen}
              onClose={() => setAssessmentDrawerOpen(false)}
              bookedEventId={sessionState.bookedEventFinalData?.id}
              bookedEventInfo={sessionState.bookedEventFinalData}
              onRunSubmitted={() => setupPage()}
              assessmentReport={sessionState.auxiliaryData?.assessmentReport}
              patientId={sessionState.bookedEventFinalData?.patientId}
              mentorId={sessionState.userRole === 'mentor' ? mentorProfileData?.id : undefined}
              onAssessmentReportSaved={() => setupPage({ hideLoader: true })}
            />
          )}

          {/* Permission Dialogs - OLD SYSTEM (for backward compatibility) */}
          {/* Only show permission dialogs for online meetings */}
          {sessionState.bookedEventFinalData && 
           legacyMeetingSrc !== 'offline' && (
            <>
              <AskBothCameraAndMicrophonePermissionsDialog
                open={devicePermissions.giveBothPermissionsDialogOpen}
                onCloseFunction={() => devicePermissions.setGiveBothPermissionsDialogOpen(false)}
                setMicrophoneError={devicePermissions.setMicrophoneError}
                setCameraError={devicePermissions.setCameraError}
                setAvailableMicrophones={devicePermissions.setAvailableMicrophones}
                setSelectedMicrophone={devicePermissions.setSelectedMicrophone}
                setAvailableSpeakers={devicePermissions.setAvailableSpeakers}
                setSelectedSpeaker={devicePermissions.setSelectedSpeaker}
                setAvailableCameras={devicePermissions.setAvailableCameras}
                setSelectedCamera={devicePermissions.setSelectedCamera}
              />
              
              <AskSinglePermissionDialog
                type={devicePermissions.giveMicrophonePermissionDialogOpen ? 'microphone' : 'camera'}
                open={devicePermissions.giveMicrophonePermissionDialogOpen || devicePermissions.giveCameraPermissionDialogOpen}
                onCloseFunction={() => {
                  devicePermissions.setGiveMicrophonePermissionDialogOpen(false)
                  devicePermissions.setGiveCameraPermissionDialogOpen(false)
                }}
                setMicrophoneError={devicePermissions.setMicrophoneError}
                setCameraError={devicePermissions.setCameraError}
                setAvailableMicrophones={devicePermissions.setAvailableMicrophones}
                setSelectedMicrophone={devicePermissions.setSelectedMicrophone}
                setAvailableSpeakers={devicePermissions.setAvailableSpeakers}
                setSelectedSpeaker={devicePermissions.setSelectedSpeaker}
                setAvailableCameras={devicePermissions.setAvailableCameras}
                setSelectedCamera={devicePermissions.setSelectedCamera}
              />
            </>
          )}

          {/* Permission Dialogs - NEW RESILIENT SYSTEM */}
          {/* Only show permission flow for online meetings */}
          {/* CRITICAL: Only render if we have session data AND it's not offline */}
          {sessionState.bookedEventFinalData && 
           legacyMeetingSrc !== 'offline' && (
            <MediaPermissionsFlow
              mediaType="both"
              sessionType="liveSession"
              showSkipOption={false}
              autoCheck={legacyMeetingSrc !== 'offline'}
              onSuccess={(permissions) => {
                // Permissions granted - device lists are already updated by the permission flow
                // No need to call checkAvailableDevices again as it would cause a loop
              }}
              onFailure={(permissions) => {
                // Permissions denied - handle silently
              }}
            />
          )}
          </>)}
        </PrescriptionDataProvider>
      </ScreeningSectionsProvider>

      {/* Modals and Other Components */}
      <PrewallAndPaywall
        id="journal_play"
        secureData={setupData}
        wallIsOpen={sessionState.prewallPaywallProps.open}
        toggleWall={(plan) => {
          sessionState.setPrewallPaywallProps((prev) => ({
            ...prev,
            open: !prev.open,
            plan: prev?.open ? null : plan,
          }))
        }}
      />

      <SEOComponent
        title="Join Your Scheduled Meeting - Secure & Confidential"
        description="Your exclusive access to a private meeting is ready. Whether you're joining online or in-person, this link is your gateway to a productive session. Please ensure you're in a quiet, private space for this appointment. Prepare to connect, discuss, and explore in a secure, confidential setting."
        image={getImageLink('MEDIA-1bfbccee9a0a484c8cf2ab6777f8c94c')}
      />

      <HeartbeatManager />
    </LSPageParentContainer>
  )
}

// Public Live Session Page (unchanged)
function PublicLiveSessionPage() {
  return (
    <LSPageParentContainer>
      <div style={{ width: '100%', flex: 1, display: 'flex', gap: '20px', marginTop: '60px' }}>
        <div
          style={{
            width: '100%',
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: '#effeff',
            borderRadius: '7px',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            By joining the session, you agree to our{' '}
            <a href={APP_CONSTANTS.APP_TERMS_URL} target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a
              href={APP_CONSTANTS.APP_PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms and Conditions
            </a>
          </div>
          <LSVideoPartSingleText style={{ cursor: 'pointer' }} underline>
            Login to Join
          </LSVideoPartSingleText>
        </div>
      </div>

      <ClubbedAuthModal
        open={true}
        onClose={() => {
          // Cannot close this dialog
        }}
        noCloseButton={true}
        loginVariant={true}
      />

      <SEOComponent
        title="Join Your Scheduled Meeting - Secure & Confidential"
        description="Your exclusive access to a private meeting is ready. Whether you're joining online or in-person, this link is your gateway to a productive session. Please ensure you're in a quiet, private space for this appointment. Prepare to connect, discuss, and explore in a secure, confidential setting."
        image={getImageLink('MEDIA-1bfbccee9a0a484c8cf2ab6777f8c94c')}
      />
    </LSPageParentContainer>
  )
}
